import { getFieldViewportAttrs } from '../utils/index.js';

import Field from './field.js';

// An abstraction to allow usage of coordinates in Yards and display in pixels

export default class FieldViewport {
    x: number;

    y: number;

    w: number;

    h: number;

    field: Field;

    flipped: boolean;

    flippedHorizontal: boolean;

    zoomFactor: number;

    dimensions: Dimensions;

    constructor(field: Field, flipped = false, zoomFactor = 1, dimensions: Dimensions, flippedHorizontal = false) {
        this.field = field;
        this.dimensions = dimensions;

        const { w, h, y, x } = getFieldViewportAttrs(dimensions, field);
        this.w = w;
        this.h = h;
        this.y = y;
        this.x = x;

        this.flipped = flipped;
        this.flippedHorizontal = flippedHorizontal;
        this.zoomFactor = zoomFactor;
    }

    // XOR of flipped and flippedHorizontal: true if exactly one is set, they cancel out if both are set
    get flippedX(): boolean {
        return this.flipped !== this.flippedHorizontal;
    }

    clampCoordsPx(coords: ScreenPoint): ScreenPoint {
        return this.getPixelCoords(this.field.clampCoords(this.getFieldCoords(coords)));
    }

    getPixelLength(w: number): number {
        return (w / this.field.getFullWidth()) * this.w * this.zoomFactor;
    }

    getPixelX(x: number): number {
        const px = this.getPixelLength(x + this.field.padding);
        if (this.flippedX) {
            return this.w * this.zoomFactor - px + this.x;
        }
        return px + this.x;
    }

    getPixelY(y: number): number {
        const py = this.getPixelLength(y + this.field.padding);
        if (this.flipped) {
            return this.h * this.zoomFactor - py + this.y;
        }
        return py + this.y;
    }

    getPixelCoords(pt: FieldPoint): ScreenPoint {
        return {
            space: 'screen',
            x: this.getPixelX(pt.x),
            y: this.getPixelY(pt.y),
        };
    }

    getPixelLine<L extends Line>(line: L): L {
        return {
            ...line,
            a: this.getPixelCoords(line.a),
            b: this.getPixelCoords(line.b),
        };
    }

    getFieldLine<L extends Line>(line: L): L {
        return {
            ...line,
            a: this.getFieldCoords(line.a),
            b: this.getFieldCoords(line.b),
        };
    }

    getFieldLength(w: number): number {
        const px = (w / this.w) * (1 / this.zoomFactor);
        const fieldX = px * this.field.getFullWidth();
        return fieldX;
    }

    getFieldX(x: number): number {
        const px = ((x - this.x) / this.w) * (1 / this.zoomFactor);
        const fieldX = px * this.field.getFullWidth();
        if (this.flippedX) {
            return this.field.getFullWidth() - fieldX - this.field.padding;
        }
        return fieldX - this.field.padding;
    }

    getFieldY(y: number): number {
        const px = ((y - this.y) / this.h) * (1 / this.zoomFactor);
        const fieldY = px * this.field.getFullHeight();
        if (this.flipped) {
            return this.field.getFullHeight() - fieldY - this.field.padding;
        }
        return fieldY - this.field.padding;
    }

    getFieldCoords(pt: ScreenPoint): FieldPoint {
        return {
            x: this.getFieldX(pt.x),
            y: this.getFieldY(pt.y),
        };
    }

    getFlippedX(px: number): number {
        return this.w * this.zoomFactor - px + this.x;
    }

    getFlippedY(py: number): number {
        return this.h * this.zoomFactor - py + this.y;
    }

    getFlippedCoords(pt: ScreenPoint): ScreenPoint {
        return {
            x: this.getFlippedX(pt.x),
            y: this.getFlippedY(pt.y),
        };
    }

    copy(): FieldViewport {
        return new FieldViewport(this.field, this.flipped, this.zoomFactor, this.dimensions, this.flippedHorizontal);
    }
}
